import React from 'react';
import { Box, FormControl, InputLabel, MenuItem, Select, Switch, TextField, Typography } from '@mui/material';
import { useOrgFeatureFlag } from '../hooks/useOrgFeatureFlags';
import type { IntegrationSettings } from '../services/integrationApi';
import { INTEGRATION_SETTINGS_COPY as COPY } from '../utils/integrationSettingsCopy';
import { NumberField } from './ui';
import {
  DEFAULT_SYNC_WEEKDAY,
  SYNC_WEEKDAYS,
  describeTimeZone,
  formatSlot,
  previewSyncSlot,
  parseSyncTime,
} from '../utils/syncTimeSlots';

export interface SyncTimeControlProps {
  settings: IntegrationSettings;
  onChange: (partial: Partial<IntegrationSettings>) => void;
  /** The store's IANA zone (`syncTimeZone` on the integration); unknown in the walkthrough. */
  timeZone?: string | null;
  /** The last slot the scheduler ran (`syncSchedule.lastSyncSlotAt`), so a slot still due shows as starting soon. */
  lastSlotAt?: string | null;
}

/** Default when a store turns the set time on: 9:00 AM, or the top of the hour for hourly. */
const DEFAULT_TIME = '09:00';

/**
 * "Start at a set time" (flag `scheduled_sync_time`, MOT-476): the local time the scheduled sync
 * starts, in the store's time zone, with the next run it gives. Renders nothing with the flag off
 * or a manual interval; the controlled settings are saved by the drawer's own Save.
 */
const SyncTimeControl: React.FC<SyncTimeControlProps> = ({ settings, onChange, timeZone, lastSlotAt }) => {
  const { enabled } = useOrgFeatureFlag('scheduled_sync_time');
  const interval = settings.syncInterval;
  if (!enabled || interval === 'manual') return null;

  const on = parseSyncTime(settings.syncTime) !== null;
  const minute = parseSyncTime(settings.syncTime)?.minute ?? 0;
  const preview = on && timeZone ? previewSyncSlot(interval, settings.syncTime, settings.syncWeekday, timeZone, lastSlotAt) : null;

  return (
    <Box sx={{ mt: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, mr: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {COPY.syncTime.label}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45 }}>
            {COPY.syncTime.desc}
          </Typography>
        </Box>
        <Switch
          size="small"
          checked={on}
          slotProps={{ input: { 'aria-label': COPY.syncTime.label } }}
          onChange={(e) =>
            onChange(
              e.target.checked
                ? {
                    syncTime: DEFAULT_TIME,
                    ...(interval === 'weekly' ? { syncWeekday: settings.syncWeekday ?? DEFAULT_SYNC_WEEKDAY } : {}),
                  }
                : { syncTime: null },
            )
          }
        />
      </Box>
      {on && (
        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start' }}>
          {interval === 'weekly' && (
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>{COPY.syncTime.weekdayLabel}</InputLabel>
              <Select
                value={settings.syncWeekday ?? DEFAULT_SYNC_WEEKDAY}
                label={COPY.syncTime.weekdayLabel}
                onChange={(e) => onChange({ syncWeekday: Number(e.target.value) })}
              >
                {SYNC_WEEKDAYS.map((day, index) => (
                  <MenuItem key={day} value={index}>
                    {day}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {interval === 'hourly' ? (
            <NumberField
              label={COPY.syncTime.minuteLabel}
              integer
              min={0}
              max={59}
              value={String(minute)}
              onChange={(val) => onChange({ syncTime: `00:${String(Math.min(59, Math.max(0, Number(val) || 0))).padStart(2, '0')}` })}
              sx={{ width: 180 }}
            />
          ) : (
            <TextField
              size="small"
              type="time"
              label={COPY.syncTime.timeLabel}
              value={settings.syncTime || ''}
              onChange={(e) => {
                if (parseSyncTime(e.target.value)) onChange({ syncTime: e.target.value });
              }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 60 }}
              sx={{ width: 150 }}
            />
          )}
        </Box>
      )}
      {on && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, lineHeight: 1.4 }}>
          {timeZone ? `${COPY.syncTime.zonePrefix} ${describeTimeZone(timeZone)}.` : COPY.syncTime.zoneUnknown}
          {preview && timeZone
            ? preview.kind === 'due'
              ? ` ${COPY.syncTime.dueNow.replace('{time}', formatSlot(preview.slot, timeZone))}`
              : ` ${COPY.syncTime.nextPrefix} ${formatSlot(preview.slot, timeZone)}.`
            : ''}
        </Typography>
      )}
    </Box>
  );
};

export default SyncTimeControl;
